import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { StarFilled, SyncOutlined } from "@ant-design/icons";
import { Col, Progress, Row, Space, Switch, Table, Tag, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EUserRole, isLanguageAllowed } from "@devowl-wp/api-real-cookie-banner";
import { useStatisticsColumns } from "./columns.js";
import { StatisticsExpansion } from "./statistics-expansion.js";
import { useContentBlockerTemplateStatistics } from "../../../../hooks/content-blocker/useContentBlockerTemplateStatistics.js";
import { useServiceTemplateStatistics } from "../../../../hooks/service/useServiceTemplateStatistics.js";
import { useTranslation } from "../../../../others/i18n.js";
import { useStores } from "../../../../store/root.js";
import { spacing } from "../../../../styles/spacing.js";
import { centered } from "../../../../styles/template.js";
import { prepareStatisticsData } from "../../../../util/statistics.js";
import { updateFilterFavoriteState } from "../../../../util/store/settings.js";
import { getSupportedLanguageOptions } from "../../../../util/template/translation.js";
import { PermissionCheck } from "../user/permission.js";
function Statistics() {
    // Generals
    const { t } = useTranslation();
    const { rcbAuthenticationStore, rcbUserSettingsStore } = useStores();
    const { settings: userSettings } = rcbUserSettingsStore;
    // UI elements, states
    const { Title } = Typography;
    const [dataSourceStats, setDataSourceStats] = useState([]);
    const [percentLoaded, setPercentLoaded] = useState(0);
    const [showFavouritesOnly, setShowFavouritesOnly] = useState(userSettings?.filter?.showFavouritesOnly !== undefined ? userSettings.filter.showFavouritesOnly : !rcbAuthenticationStore.hasRole(EUserRole.Creator));
    // Query
    const toggleFavourites = useCallback(()=>{
        setPercentLoaded(0);
        setDataSourceStats([]);
        updateFilterFavoriteState(!showFavouritesOnly, rcbUserSettingsStore);
        setShowFavouritesOnly(!showFavouritesOnly);
    }, [
        showFavouritesOnly
    ]);
    const contentBlockerStatistics = useContentBlockerTemplateStatistics(showFavouritesOnly);
    const serviceStatistics = useServiceTemplateStatistics(showFavouritesOnly);
    const languages = useMemo(()=>getSupportedLanguageOptions(t).filter((lang)=>isLanguageAllowed(rcbAuthenticationStore.session?.user, lang.language)), [
        t,
        rcbAuthenticationStore.session?.user
    ]);
    useEffect(()=>{
        // Simple loader calculation
        const contentStatisticReady = contentBlockerStatistics.isSuccess && !contentBlockerStatistics.isFetching && contentBlockerStatistics.data;
        const serviceStatisticReady = serviceStatistics.isSuccess && !serviceStatistics.isFetching && serviceStatistics.data;
        if (contentStatisticReady && serviceStatisticReady && dataSourceStats.length === 0) {
            const statistics = [
                contentBlockerStatistics.data.statistics.contentBlocker,
                serviceStatistics.data.statistics.services
            ].filter(Boolean);
            const preparedStatistics = prepareStatisticsData(languages, statistics);
            setDataSourceStats(preparedStatistics);
        }
    }, [
        contentBlockerStatistics,
        serviceStatistics,
        toggleFavourites
    ]);
    // loader
    useEffect(()=>{
        let interval = null;
        if ((contentBlockerStatistics.isFetching || serviceStatistics.isFetching) && percentLoaded < 99) {
            interval = setInterval(()=>{
                setPercentLoaded((prevCount)=>prevCount + 1);
            }, 11 * 1000 / 100);
        }
        return ()=>{
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [
        contentBlockerStatistics,
        serviceStatistics,
        toggleFavourites
    ]);
    const columns = useStatisticsColumns(t);
    return /*#__PURE__*/ _jsxs(PermissionCheck, {
        minRole: EUserRole.Translator,
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                children: [
                    /*#__PURE__*/ _jsx(Col, {
                        children: /*#__PURE__*/ _jsx(Title, {
                            level: 3,
                            children: t("Statistics")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        children: /*#__PURE__*/ _jsxs(Space, {
                            style: {
                                paddingTop: spacing.layout.content.paddingSmall
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Switch, {
                                    defaultChecked: showFavouritesOnly,
                                    checkedChildren: /*#__PURE__*/ _jsx(StarFilled, {
                                        style: {
                                            color: "orange"
                                        }
                                    }),
                                    onClick: ()=>toggleFavourites(),
                                    style: {
                                        float: "left",
                                        marginLeft: "1rem"
                                    }
                                }),
                                " ",
                                t("Favourites Only")
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Row, {
                style: {
                    paddingBottom: spacing.layout.content.paddingSmall
                },
                children: /*#__PURE__*/ _jsx(Col, {
                    children: /*#__PURE__*/ _jsxs(Space, {
                        size: [
                            8,
                            8
                        ],
                        wrap: true,
                        children: [
                            /*#__PURE__*/ _jsx(Tag, {
                                color: showFavouritesOnly ? "orange" : "grey",
                                children: showFavouritesOnly ? t("Favorites only") : t("All templates")
                            }),
                            /*#__PURE__*/ _jsxs(Tag, {
                                color: "blue",
                                children: [
                                    serviceStatistics.data ? serviceStatistics.data.statistics.services.calculatedIdentifier.length : /*#__PURE__*/ _jsx(SyncOutlined, {
                                        spin: true
                                    }),
                                    " ",
                                    t("Services")
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Tag, {
                                color: "blue",
                                children: [
                                    contentBlockerStatistics.data ? contentBlockerStatistics.data.statistics.contentBlocker.calculatedIdentifier.length : /*#__PURE__*/ _jsx(SyncOutlined, {
                                        spin: true
                                    }),
                                    " ",
                                    t("Content Blocker")
                                ]
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(Row, {
                style: {
                    paddingBottom: "1rem"
                },
                hidden: !contentBlockerStatistics.isFetching && !serviceStatistics.isFetching,
                children: /*#__PURE__*/ _jsxs(Col, {
                    style: centered,
                    children: [
                        /*#__PURE__*/ _jsx(Progress, {
                            width: 100,
                            type: "circle",
                            percent: percentLoaded,
                            size: "small"
                        }),
                        /*#__PURE__*/ _jsx(Typography.Title, {
                            level: 3,
                            children: t("Collecting data...")
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Row, {
                style: {
                    paddingBottom: "1rem"
                },
                hidden: contentBlockerStatistics.isFetching || serviceStatistics.isFetching,
                children: /*#__PURE__*/ _jsx(Col, {
                    style: {
                        width: "100%"
                    },
                    children: /*#__PURE__*/ _jsx(Table, {
                        rowKey: "language",
                        columns: columns,
                        dataSource: dataSourceStats,
                        pagination: false,
                        expandable: {
                            expandedRowRender: (tableData)=>/*#__PURE__*/ _jsx(StatisticsExpansion, {
                                    tableData: tableData
                                })
                        }
                    })
                })
            })
        ]
    });
}
export { Statistics };
